import { z } from 'zod';
import type { CommsInboxFilter } from '@yrp/shared/comms';
import {
  FlagSearchParam,
  IsoDateSearchParam,
  PositiveIntegerSearchParam,
  enumSearchParam,
  searchValidator,
} from '../../lib/search-params.js';

/**
 * Search params for the Phone & Text routes — four SEPARATE schemas, one per
 * route, never a shared generic thread param (Rule 13). The channel is chosen
 * by the route path: `/comms` is 1:1 keyed by `contactId`, `/comms/groups` by
 * `groupId`, `/comms/messenger` by `threadId`. Each z.object strips the other
 * routes' keys, so a 1:1 contact id can never be read by the group page or
 * the other way round.
 *
 * Imported by router.tsx (entry chunk): zod and the search-param helpers only,
 * `@yrp/shared` as types. comms-search.test.ts checks the lists against the
 * shared enums at run time.
 *
 * Never in these URLs: phone numbers or phone digits (an unmatched row is
 * `unmatched=1` plus a per-mount history-state counter), names, message text,
 * the inbox search box, drafts or recipients.
 */

// ---- /comms (1:1) -----------------------------------------------------------

/** Inbox filter chips other than the default (All, the absent key). */
export const COMMS_INBOX_FILTERS = ['unread', 'calls', 'missed', 'scheduled'] as const satisfies ReadonlyArray<
  Exclude<CommsInboxFilter, 'all'>
>;
const _commsFiltersComplete: [Exclude<CommsInboxFilter, 'all' | (typeof COMMS_INBOX_FILTERS)[number]>] extends [never]
  ? true
  : never = true;
void _commsFiltersComplete;

export const CommsSearchSchema = z.object({
  /** The open 1:1 conversation's CRM contact (int4-capped, so phone digits never pass). */
  contactId: PositiveIntegerSearchParam,
  /** An unmatched (no-contact) row is open; which one is a history-state counter, never digits. */
  unmatched: FlagSearchParam,
  filter: enumSearchParam(z.enum(COMMS_INBOX_FILTERS)),
  /** Date range, YYYY-MM-DD. */
  from: IsoDateSearchParam,
  to: IsoDateSearchParam,
});

const parseCommsSearch = searchValidator(CommsSearchSchema);

/**
 * `validateSearch` for `/comms`. Never throws; unknown keys are stripped.
 * `contactId` and `unmatched` are exclusive: with both, the contact wins and
 * `unmatched` comes back as an explicit undefined (TanStack spreads the raw
 * search under the validated one, so only an explicit undefined removes it).
 */
export function commsSearch(search: Record<string, unknown>): z.output<typeof CommsSearchSchema> {
  const parsed = parseCommsSearch(search);
  if (parsed.contactId !== undefined && parsed.unmatched !== undefined) return { ...parsed, unmatched: undefined };
  return parsed;
}
export type CommsSearch = ReturnType<typeof commsSearch>;

// ---- /comms/groups (group texts) --------------------------------------------

export const CommsGroupsSearchSchema = z.object({
  /** The open group thread (comms_group_threads.id). */
  groupId: PositiveIntegerSearchParam,
});

/** `validateSearch` for `/comms/groups` only. Never throws. */
export const commsGroupsSearch = searchValidator(CommsGroupsSearchSchema);
export type CommsGroupsSearch = ReturnType<typeof commsGroupsSearch>;

// ---- /comms/messenger (Facebook Messenger) ----------------------------------

export const CommsMessengerSearchSchema = z.object({
  /** The open Messenger thread. */
  threadId: PositiveIntegerSearchParam,
});

/** `validateSearch` for `/comms/messenger` only. Never throws. */
export const commsMessengerSearch = searchValidator(CommsMessengerSearchSchema);
export type CommsMessengerSearch = ReturnType<typeof commsMessengerSearch>;

// ---- /voicemail ---------------------------------------------------------------

/** Voicemail list filters other than the default (Inbox, the absent key). */
export const VOICEMAIL_FILTERS = ['unlistened', 'archived'] as const;
export type VoicemailFilter = (typeof VOICEMAIL_FILTERS)[number];

export const VoicemailSearchSchema = z.object({
  /** The highlighted voicemail. */
  voicemailId: PositiveIntegerSearchParam,
  filter: enumSearchParam(z.enum(VOICEMAIL_FILTERS)),
});

/** `validateSearch` for `/voicemail`. Never throws. */
export const voicemailSearch = searchValidator(VoicemailSearchSchema);
export type VoicemailSearch = ReturnType<typeof voicemailSearch>;
